import type { SortDirection } from '@tanstack/react-table'
import clsx from 'clsx'
import { ArrowUpNarrowWide, ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { Button } from 'primitives/button'
import { Tooltip } from 'primitives/tooltip'
import type { DataTableHeader, DataTableInstance, DataTableRowData } from '../../types'
import { dataVariable } from '../../utils/style.utils'

interface Props<TData extends DataTableRowData> {
  header: DataTableHeader<TData>
  table: DataTableInstance<TData>
}

type SortedType = SortDirection | false

interface SortedTypeProps {
  sorted: SortedType
}

const SortedIcon = ({ sorted }: SortedTypeProps) => {
  const getSortedIcon = () => {
    switch (sorted) {
      case 'desc':
        return <ChevronDownIcon size="100%" />
      case 'asc':
        return <ChevronUpIcon size="100%" />
      default:
        return <ArrowUpNarrowWide size="100%" />
    }
  }
  return <>{getSortedIcon()}</>
}

export const TableHeadCellSortLabel = <TData extends DataTableRowData>({
  header,
  table,
  ...rest
}: Props<TData>) => {
  const {
    options: { localization },
  } = table
  const { column } = header
  const { columnDef } = column

  const sorted = column.getIsSorted()

  const sortTooltip = sorted
    ? sorted === 'desc'
      ? localization.sortedByColumnDesc.replace('{column}', columnDef.header)
      : localization.sortedByColumnAsc.replace('{column}', columnDef.header)
    : column.getNextSortingOrder() === 'desc'
      ? localization.sortByColumnDesc.replace('{column}', columnDef.header)
      : localization.sortByColumnAsc.replace('{column}', columnDef.header)

  const SortActionButton = (
    <Button
      variant="ghost"
      aria-label={sortTooltip}
      {...dataVariable('sorted', sorted)}
      {...rest}
      className={clsx(
        'mrt-table-head-sort-button h-4 w-4 transition-all duration-150 ease-in-out [&[data-sorted]]:text-[var(--mantine-primary-color-filled)]',
      )}
      size="icon"
    >
      <SortedIcon sorted={sorted} />
    </Button>
  )

  return <Tooltip content={sortTooltip}>{SortActionButton}</Tooltip>
}
